import * as React from "react";
import { Archive, ArrowUpDown, ChevronDown, Search, Sparkles, Upload, X } from "lucide-react";
import { ignorePopoverFocusOutOnChange } from "@/components/reviews/ignorePopoverFocusOut";
import { Button, DropdownMenu, Input, InputGroup, Popover, SimpleSelect, Tooltip } from "@/components/ui";
import { UPLOAD_TYPES } from "@/containers/Coach/DesignStudio/hooks/useBackgroundMutations";
import { cn } from "@/lib/utils";
import {
  activeFilterCount,
  appliedFilters,
  clearPopoverFilters,
  LibraryFilters,
  LibraryMode,
  LibraryScope,
  PreviewRatio,
} from "./libraryFilterParams";
import { FiltersButton, LibraryFiltersPopover } from "./LibraryFiltersPopover";

export { DEFAULT_COACH_FILTERS, DEFAULT_LIBRARY_FILTERS, librarySearchBody } from "./libraryFilterParams";
export type { LibraryFilters, LibraryFlag, LibraryMode, LibraryScope, PreviewRatio } from "./libraryFilterParams";

const SORT_ITEMS: { value: LibraryFilters["sort"]; label: string }[] = [
  { value: "recent", label: "Newest" },
  { value: "popular", label: "Most used" },
];
export const PREVIEW_RATIO_ITEMS: { value: PreviewRatio; label: string }[] = [
  { value: "square_1_1", label: "1:1" },
  { value: "portrait_4_5", label: "4:5" },
  { value: "portrait_9_16", label: "9:16" },
];

/** A labeled `SimpleSelect`; the label names the group, since the select's trigger takes no label. */
export const OptionSelect: React.FC<{
  label: string;
  items: { value: string; label: string }[];
  value: string;
  onChange(value: string): void;
}> = ({ label, items, value, onChange }) => (
  <div
    role="group"
    aria-label={label}
    className="flex h-9 items-center rounded-md border border-input bg-white px-3 text-sm"
  >
    <SimpleSelect items={items} value={value} onValueChange={next => onChange(next ?? "")} />
  </div>
);

export interface LibraryActionsProps {
  /** A coach library needs a coach to write into. */
  canWrite: boolean;
  onGenerate(): void;
  onUpload(files: File[]): void;
  uploading: boolean;
  /** Lets the file chooser pick several photos at once. */
  multiple?: boolean;
}

/** Create with AI and Upload, shown in the section header. */
export const LibraryActions: React.FC<LibraryActionsProps> = ({
  canWrite,
  onGenerate,
  onUpload,
  uploading,
  multiple = false,
}) => {
  const inputRef = React.useRef<HTMLInputElement>(null);

  const onFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    event.target.value = "";
    if (files.length > 0) onUpload(files);
  };

  return (
    <>
      <Button variant="outline" onClick={() => inputRef.current?.click()} disabled={!canWrite} loading={uploading}>
        <Upload aria-hidden="true" />
        Upload
      </Button>
      <Button onClick={onGenerate} disabled={!canWrite}>
        <Sparkles aria-hidden="true" />
        Create with AI
      </Button>
      <input
        ref={inputRef}
        type="file"
        accept={UPLOAD_TYPES.join(",")}
        multiple={multiple}
        className="hidden"
        onChange={onFile}
        data-testid="library-upload-input"
      />
    </>
  );
};

export interface LibraryToolbarProps {
  mode: LibraryMode;
  filters: LibraryFilters;
  onFiltersChange(filters: LibraryFilters): void;
  /** Coach mode: Only mine is `coach`, off is `all`. The admin header owns the scope instead. */
  scope?: LibraryScope;
  onScopeChange?(scope: LibraryScope): void;
  /** Coach mode: Include archived, which only exists while Only mine is on. */
  onIncludeArchivedChange?(on: boolean): void;
  /** The filtered total for the popover's footer; null while it loads. */
  resultCount?: number | null;
}

const SEARCH_DEBOUNCE_MS = 300;

// Mini frames at each shape, all 16px tall so they compare at a glance.
const FRAME_WIDTH: Record<PreviewRatio, string> = {
  square_1_1: "w-4",
  portrait_4_5: "w-[13px]",
  portrait_9_16: "w-[9px]",
};

const PreviewSizeToggle: React.FC<{ value: PreviewRatio; onChange(value: PreviewRatio): void }> = ({
  value,
  onChange,
}) => (
  <div
    className="flex h-9 items-center rounded-lg border border-slate-200 bg-white p-0.5"
    role="radiogroup"
    aria-label="Preview size"
  >
    {PREVIEW_RATIO_ITEMS.map(item => {
      const selected = item.value === value;
      return (
        <Tooltip key={item.value}>
          <Tooltip.Trigger
            render={
              <button
                type="button"
                role="radio"
                aria-checked={selected}
                aria-label={`Preview at ${item.label}`}
                onClick={() => onChange(item.value)}
                className={cn(
                  "flex h-full w-8 items-center justify-center rounded-md transition-colors cursor-pointer",
                  selected ? "bg-slate-100 text-slate-900" : "text-slate-400 hover:text-slate-700"
                )}
              />
            }
          >
            <span
              aria-hidden="true"
              className={cn("block h-4 rounded-[3px] border-[1.5px] border-current", FRAME_WIDTH[item.value])}
            />
          </Tooltip.Trigger>
          <Tooltip.Content>Preview at {item.label}</Tooltip.Content>
        </Tooltip>
      );
    })}
  </div>
);

const SortMenu: React.FC<{ value: LibraryFilters["sort"]; onChange(value: LibraryFilters["sort"]): void }> = ({
  value,
  onChange,
}) => (
  <DropdownMenu>
    <DropdownMenu.Trigger
      render={
        <Button
          variant="outline"
          className="h-9 bg-white"
          aria-label={`Sort: ${SORT_ITEMS.find(i => i.value === value)?.label}`}
        >
          <ArrowUpDown aria-hidden="true" className="text-slate-400" />
          {SORT_ITEMS.find(item => item.value === value)?.label}
          <ChevronDown aria-hidden="true" className="text-slate-400" />
        </Button>
      }
    />
    <DropdownMenu.Content align="end">
      <DropdownMenu.RadioGroup value={value} onValueChange={next => onChange(next as LibraryFilters["sort"])}>
        {SORT_ITEMS.map(item => (
          <DropdownMenu.RadioItem key={item.value} value={item.value}>
            {item.label}
          </DropdownMenu.RadioItem>
        ))}
      </DropdownMenu.RadioGroup>
    </DropdownMenu.Content>
  </DropdownMenu>
);

const ACTIVE = "bg-slate-100 border-slate-400 text-slate-900 hover:bg-slate-100";

/** A pill with a small switch inside: reads as on or off at a glance. */
const OnlyMineToggle: React.FC<{ on: boolean; onChange(on: boolean): void }> = ({ on, onChange }) => (
  <Button
    variant="outline"
    aria-pressed={on}
    onClick={() => onChange(!on)}
    className={cn("h-9 rounded-full bg-white pl-2", on && ACTIVE)}
  >
    <span
      aria-hidden="true"
      className={cn(
        "relative h-4 w-7 shrink-0 rounded-full transition-colors duration-200",
        on ? "bg-primary" : "bg-slate-300"
      )}
    >
      <span
        className={cn(
          "absolute top-0.5 left-0.5 size-3 rounded-full bg-white shadow-sm transition-transform duration-200 motion-reduce:transition-none",
          on && "translate-x-3"
        )}
      />
    </span>
    Only mine
  </Button>
);

/** Slides out beside Only mine; archived backgrounds only exist in your own library. */
const IncludeArchivedChip: React.FC<{ shown: boolean; on: boolean; onChange(on: boolean): void }> = ({
  shown,
  on,
  onChange,
}) => (
  <div
    className={cn(
      "overflow-hidden transition-[max-width,opacity] duration-300 ease-out motion-reduce:transition-none",
      shown ? "max-w-48 opacity-100" : "max-w-0 opacity-0"
    )}
    aria-hidden={!shown}
  >
    <Button
      variant="outline"
      aria-pressed={on}
      tabIndex={shown ? undefined : -1}
      onClick={() => onChange(!on)}
      className={cn("h-9 rounded-full bg-white whitespace-nowrap", on && ACTIVE)}
    >
      <Archive aria-hidden="true" />
      Include archived
    </Button>
  </div>
);

const AppliedFilters: React.FC<{
  mode: LibraryMode;
  filters: LibraryFilters;
  onFiltersChange(filters: LibraryFilters): void;
}> = ({ mode, filters, onFiltersChange }) => {
  const pills = appliedFilters(filters, mode);
  const shown = pills.length > 0;
  return (
    <div
      className={cn(
        "grid transition-[grid-template-rows,opacity] duration-300 ease-out motion-reduce:transition-none",
        shown ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
      )}
      aria-hidden={!shown}
    >
      <div className="min-h-0 overflow-hidden">
        <ul className="flex flex-wrap items-center gap-1.5 pt-0.5" aria-label="Applied filters">
          {pills.map(pill => (
            <li
              key={pill.key}
              className="inline-flex h-7 items-center gap-1 rounded-full bg-slate-100 pl-2.5 pr-1 text-xs font-medium text-slate-700"
            >
              {pill.label}
              <button
                type="button"
                aria-label={`Remove ${pill.label}`}
                onClick={() => onFiltersChange(pill.remove(filters))}
                className="flex size-5 items-center justify-center rounded-full text-slate-400 hover:bg-slate-200 hover:text-slate-700 cursor-pointer"
              >
                <X className="size-3" aria-hidden="true" />
              </button>
            </li>
          ))}
          {shown && (
            <li>
              <Button
                variant="link"
                size="inline"
                className="text-xs"
                onClick={() => onFiltersChange(clearPopoverFilters(filters, mode))}
              >
                Clear all
              </Button>
            </li>
          )}
        </ul>
      </div>
    </div>
  );
};

/**
 * One quiet row (search, Only mine for coaches, Filters with a count, Sort, Preview size), then the
 * applied filters as pills once there are any. Everything else lives in the Filters popover.
 */
export const LibraryToolbar: React.FC<LibraryToolbarProps> = ({
  mode,
  filters,
  onFiltersChange,
  scope = "all",
  onScopeChange,
  onIncludeArchivedChange,
  resultCount,
}) => {
  const [search, setSearch] = React.useState(filters.q);
  const [filtersOpen, setFiltersOpen] = React.useState(false);
  const latest = React.useRef({ filters, onFiltersChange });
  latest.current = { filters, onFiltersChange };

  React.useEffect(() => {
    const timer = setTimeout(() => {
      const q = search.trim();
      if (q !== latest.current.filters.q) latest.current.onFiltersChange({ ...latest.current.filters, q });
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search]);

  // Search cleared from outside (Clear filters, a link): the box follows.
  React.useEffect(() => {
    setSearch(current => (current.trim() === filters.q ? current : filters.q));
  }, [filters.q]);

  const isCoach = mode === "coach";
  const mine = scope === "coach";
  const count = activeFilterCount(filters, mode);
  const setSort = (sort: LibraryFilters["sort"]) => onFiltersChange({ ...filters, sort });
  const setPreview = (previewRatio: PreviewRatio) => onFiltersChange({ ...filters, previewRatio });

  return (
    <div className="flex flex-col gap-2" data-testid="library-toolbar">
      <div className="flex flex-wrap items-center gap-2">
        <InputGroup
          className="min-w-full flex-1 sm:min-w-64"
          control={controlProps => (
            <Input
              {...controlProps}
              value={search}
              placeholder="Search backgrounds…"
              aria-label="Search backgrounds"
              onChange={event => setSearch(event.target.value)}
            />
          )}
          prepend={<Search />}
        />
        {isCoach && (
          <div className="flex items-center gap-2">
            <OnlyMineToggle on={mine} onChange={on => onScopeChange?.(on ? "coach" : "all")} />
            <IncludeArchivedChip
              shown={mine}
              on={filters.visibility.includes("archived")}
              onChange={on => onIncludeArchivedChange?.(on)}
            />
          </div>
        )}
        <Popover open={filtersOpen} onOpenChange={ignorePopoverFocusOutOnChange(setFiltersOpen)}>
          <Popover.Trigger render={<FiltersButton count={count} />} />
          <LibraryFiltersPopover
            mode={mode}
            filters={filters}
            onFiltersChange={onFiltersChange}
            resultCount={resultCount}
            onDone={() => setFiltersOpen(false)}
            view={
              <div className="flex flex-wrap items-center gap-2">
                <SortMenu value={filters.sort} onChange={setSort} />
                <PreviewSizeToggle value={filters.previewRatio} onChange={setPreview} />
              </div>
            }
          />
        </Popover>
        {/* Not filters: they change the order and the crop, never what's found. */}
        <div className="ml-auto hidden items-center gap-2 sm:flex">
          <SortMenu value={filters.sort} onChange={setSort} />
          <PreviewSizeToggle value={filters.previewRatio} onChange={setPreview} />
        </div>
      </div>
      <AppliedFilters mode={mode} filters={filters} onFiltersChange={onFiltersChange} />
    </div>
  );
};
